// PostgREST client that mirrors the Supabase JS SDK's builder pattern.
//
// Components import { supabase } from '../lib/api' and use the same
// .from().select().eq().order().single() chains they used with Supabase.
// Under the hood, this translates to PostgREST REST calls with the Okta
// ID token as the Authorization header.
//
// The token is set once by UserContext after Okta login.
//
// Errors: surfaces unexpected failures via showError (toast). Components
// can still inspect the returned { data, error } to handle errors locally
// (e.g., to skip a flash message), but users always see *something* when
// a request fails — no more silent failures.

import { showError } from './toast';

const API_BASE = process.env.REACT_APP_API_BASE_URL;
const SESSION_EXPIRED_EVENT = 'myhr-session-expired';

let _token = null;

export function setAuthToken(token) {
  _token = token;
}

export function getAuthToken() {
  return _token;
}

function headers(extra = {}) {
  const h = { 'Content-Type': 'application/json', ...extra };
  if (_token) h['Authorization'] = `Bearer ${_token}`;
  return h;
}

// Translate a chain of builder calls into a PostgREST URL + fetch options.
class QueryBuilder {
  constructor(table) {
    this._table = table;
    this._silent = false;
    this._params = [];
    this._select = null;
    this._single = false;
    this._method = 'GET';
    this._body = null;
    this._prefer = [];
  }

  select(columns = '*') {
    this._select = columns;
    return this;
  }

  eq(column, value) {
    this._params.push(`${column}=eq.${encodeURIComponent(value)}`);
    return this;
  }

  in(column, values) {
    const list = values.map(v => encodeURIComponent(v)).join(',');
    this._params.push(`${column}=in.(${list})`);
    return this;
  }

  neq(column, value) {
    this._params.push(`${column}=neq.${encodeURIComponent(value)}`);
    return this;
  }

  gt(column, value) {
    this._params.push(`${column}=gt.${encodeURIComponent(value)}`);
    return this;
  }

  gte(column, value) {
    this._params.push(`${column}=gte.${encodeURIComponent(value)}`);
    return this;
  }

  lt(column, value) {
    this._params.push(`${column}=lt.${encodeURIComponent(value)}`);
    return this;
  }

  lte(column, value) {
    this._params.push(`${column}=lte.${encodeURIComponent(value)}`);
    return this;
  }

  is(column, value) {
    this._params.push(`${column}=is.${value}`);
    return this;
  }

  // PostgREST OR: ?or=(a.eq.1,b.eq.2,and(c.is.null,d.eq.3)). The expression
  // is the caller's literal filter list (without the wrapping parens).
  // We don't URL-encode the expression here because PostgREST's filter
  // operators (`.`, `,`, `(`, `)`, `=`) must remain literal — encoding would
  // corrupt them. The browser fetch impl percent-encodes the few unsafe
  // chars (e.g. `"`) inside the URL automatically. Callers should double-quote
  // values that contain `,` or parens (use the same shape that supabase-js
  // expects).
  or(expression) {
    this._params.push(`or=(${expression})`);
    return this;
  }

  order(column, { ascending = true } = {}) {
    this._params.push(`order=${column}.${ascending ? 'asc' : 'desc'}`);
    return this;
  }

  limit(count) {
    this._params.push(`limit=${count}`);
    return this;
  }

  single() {
    this._single = true;
    this._params.push('limit=1');
    return this;
  }

  // Supabase-js alias for .single(). Our .single() already maps 0 rows
  // to { data: null, error: null } (see the 406 handling below), which
  // matches what maybeSingle() callers expect. Exposing the alias keeps
  // supabase-style chains from crashing with "x.maybeSingle is not a
  // function".
  maybeSingle() {
    return this.single();
  }

  // --- Mutations ---

  insert(rows) {
    this._method = 'POST';
    this._body = Array.isArray(rows) ? rows : [rows];
    this._prefer.push('return=representation');
    return this;
  }

  update(values) {
    this._method = 'PATCH';
    this._body = values;
    this._prefer.push('return=representation');
    return this;
  }

  upsert(rows) {
    this._method = 'POST';
    this._body = Array.isArray(rows) ? rows : [rows];
    this._prefer.push('return=representation', 'resolution=merge-duplicates');
    return this;
  }

  delete() {
    this._method = 'DELETE';
    this._prefer.push('return=representation');
    return this;
  }

  // Suppress the global error toast for this request. Use when the
  // caller already handles the error UX (e.g., inline form validation,
  // a friendly flash message). The error is still returned in the
  // { data, error } shape.
  silent() {
    this._silent = true;
    return this;
  }

  // --- Execute ---

  async then(resolve, reject) {
    try {
      const result = await this._execute();
      resolve(result);
    } catch (err) {
      reject(err);
    }
  }

  async _execute() {
    const params = [...this._params];
    if (this._select) params.unshift(`select=${encodeURIComponent(this._select)}`);

    const qs = params.length ? `?${params.join('&')}` : '';
    const url = `${API_BASE}/${this._table}${qs}`;

    const reqHeaders = headers();
    if (this._single) {
      reqHeaders['Accept'] = 'application/vnd.pgrst.object+json';
    }
    if (this._prefer.length) {
      reqHeaders['Prefer'] = this._prefer.join(', ');
    }

    const opts = { method: this._method, headers: reqHeaders };
    if (this._body) opts.body = JSON.stringify(this._body);

    try {
      const res = await fetch(url, opts);
      // PostgREST returns 406 with .single() when 0 rows match. Supabase
      // returns { data: null, error: null } in that case — match that.
      if (res.status === 406 && this._single) {
        return { data: null, error: null };
      }
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        const error = { message: errBody.message || res.statusText, code: errBody.code, status: res.status };

        // 401 always fires the session-expired event regardless of
        // silent mode — re-auth isn't optional.
        if (res.status === 401 && typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
        } else if (!this._silent) {
          if (res.status === 403) {
            showError("You don't have permission to do that.");
          } else if (res.status >= 500) {
            showError(`Server error (${res.status}). Please try again in a moment.`);
          } else if (res.status !== 401) {
            // 4xx other than 401/403 — usually a bad request, surface the message
            showError(error.message || `Request failed (${res.status}).`);
          }
        }

        return { data: null, error };
      }
      const text = await res.text();
      let data = null;
      if (text) {
        try {
          data = JSON.parse(text);
        } catch (parseErr) {
          if (!this._silent) {
            showError('Server returned an unexpected response format.');
          }
          return {
            data: null,
            error: {
              message: `Invalid JSON response: ${parseErr.message}`,
              code: 'PARSE_ERROR',
              status: res.status,
            },
          };
        }
      }
      return { data, error: null };
    } catch (err) {
      // The fetch itself failed — network down, DNS, CORS, etc.
      // (Parse errors are caught above, not here.)
      if (!this._silent) {
        showError('Network error — check your connection and try again.');
      }
      return {
        data: null,
        error: { message: err.message, code: 'NETWORK_ERROR', status: 0 },
      };
    }
  }
}

export const SESSION_EXPIRED = SESSION_EXPIRED_EVENT;

// The public API — mirrors the Supabase client shape.
export const supabase = {
  from(table) {
    return new QueryBuilder(table);
  },
};
